<template>
  <div class="timeline-overview">
    <div
      ref="timelineShell"
      class="timeline-shell"
      @click="handleTimelineClick"
      @mouseleave="handlePointerLeave"
    >
      <div class="timeline-lane timeline-lane-primary"></div>
      <div class="timeline-lane timeline-lane-secondary"></div>
      <span
        v-for="tick in overviewTicks"
        :key="tick.label"
        class="timeline-gridline"
        :style="{ left: `${tick.positionPct}%` }"
      ></span>

      <button
        v-for="group in visibleTimelineMarkers"
        :key="group.id"
        type="button"
        class="timeline-marker"
        :class="markerClasses(group)"
        :style="markerStyle(group)"
        @mouseenter="showTooltip(group)"
        @focus="showTooltip(group)"
        @blur="hideTooltip"
        @click.stop="handleMarkerClick(group)"
      >
        <span class="timeline-marker-core"></span>
        <span class="timeline-marker-label">
          <span v-if="group.events.length > 1" class="timeline-marker-count">{{ group.events.length }}</span>
          <span v-else class="timeline-marker-time">{{ formatDuration(group.centerSeconds) }}</span>
        </span>
      </button>

      <div
        v-if="tooltipGroup"
        ref="timelineTooltip"
        class="timeline-tooltip"
        :style="tooltipStyle"
        @mouseleave="handlePointerLeave"
        @click.stop="selectTooltipGroup"
      >
        <div class="timeline-tooltip-frame">
          <video
            v-if="activeVideoUrl"
            ref="previewVideo"
            class="timeline-tooltip-video"
            muted
            playsinline
            preload="metadata"
            :src="activeVideoUrl"
            @loadedmetadata="syncPreviewFrame"
            @seeked="previewReady = true"
          ></video>
          <div v-if="!activeVideoUrl" class="timeline-tooltip-fallback">
            Preview unavailable
          </div>
          <div v-if="activeVideoUrl && !previewReady" class="timeline-tooltip-loading">
            Loading preview
          </div>
        </div>
        <div class="timeline-tooltip-body">
          <div class="timeline-tooltip-row">
            <span class="timeline-tooltip-timestamp">{{ formatDuration(tooltipGroup.centerSeconds) }}</span>
            <span class="timeline-tooltip-badge">{{ tooltipGroup.events.length }} {{ tooltipGroup.events.length === 1 ? 'match' : 'matches' }}</span>
          </div>
          <p class="timeline-tooltip-title">{{ tooltipTitle }}</p>
          <p class="timeline-tooltip-copy">
            {{ formatDuration(tooltipGroup.startSeconds) }} - {{ formatDuration(tooltipGroup.endSeconds) }}
          </p>
        </div>
      </div>
    </div>

    <div class="timeline-scale timeline-scale-primary" aria-hidden="true">
      <span
        v-for="tick in overviewTicks"
        :key="`scale-${tick.label}`"
        class="timeline-scale-tick"
      >
        {{ tick.label }}
      </span>
    </div>

    <button
      v-if="isZoomed"
      type="button"
      class="timeline-zoom-reset"
      @click="resetZoom"
    >
      Reset zoom
    </button>
  </div>
</template>

<script>
export default {
  name: 'CeremonyTimelineOverview',
  props: {
    timelineDurationSeconds: {
      type: Number,
      default: 0,
    },
    clusteredTimelineMarkers: {
      type: Array,
      default: () => [],
    },
    activeVideoUrl: {
      type: String,
      default: '',
    },
    formatDuration: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      tooltipGroup: null,
      tooltipLeftPct: 0,
      previewReady: false,
      viewStartSeconds: 0,
      viewEndSeconds: 1,
      zoomedGroupId: '',
    }
  },
  computed: {
    safeDuration() {
      return Math.max(1, Number(this.timelineDurationSeconds || 1))
    },
    viewDurationSeconds() {
      return Math.max(1, this.viewEndSeconds - this.viewStartSeconds)
    },
    isZoomed() {
      return this.viewStartSeconds > 0 || this.viewEndSeconds < this.safeDuration
    },
    overviewTicks() {
      const steps = 4
      return Array.from({ length: steps + 1 }, (_, index) => {
        const ratio = index / steps
        return {
          label: this.formatDuration(this.viewStartSeconds + (this.viewDurationSeconds * ratio)),
          positionPct: ratio * 100,
        }
      })
    },
    visibleTimelineMarkers() {
      return (this.clusteredTimelineMarkers || [])
        .filter(group => group.endSeconds >= this.viewStartSeconds && group.startSeconds <= this.viewEndSeconds)
        .map(group => {
          const relativeCenter = group.centerSeconds - this.viewStartSeconds
          const positionPct = Math.min(100, Math.max(0, (relativeCenter / this.viewDurationSeconds) * 100))
          return {
            ...group,
            positionPct,
          }
        })
    },
    tooltipStyle() {
      return {
        left: `clamp(5.5rem, ${this.tooltipLeftPct}%, calc(100% - 5.5rem))`,
      }
    },
    tooltipTitle() {
      if (!this.tooltipGroup?.events?.length) return ''
      const [firstEvent] = this.tooltipGroup.events
      if (this.tooltipGroup.events.length === 1) {
        return firstEvent.title
      }
      return `${firstEvent.title} +${this.tooltipGroup.events.length - 1} nearby result(s)`
    },
  },
  watch: {
    activeVideoUrl() {
      this.previewReady = false
    },
    timelineDurationSeconds: {
      immediate: true,
      handler() {
        this.resetZoom()
      },
    },
  },
  methods: {
    markerClasses(group) {
      return {
        'timeline-marker-active': group.isActive,
        'timeline-marker-selected': group.isSelected,
        'timeline-marker-group': group.events.length > 1,
      }
    },
    markerStyle(group) {
      const markerWidth = this.getMarkerWidth(group)
      return {
        left: `${group.positionPct}%`,
        width: `${markerWidth}rem`,
      }
    },
    getMarkerWidth(group) {
      const eventCount = Number(group?.events?.length || 1)
      return Math.min(2.5, 0.875 + (Math.max(eventCount - 1, 0) * 0.25))
    },
    showTooltip(group) {
      this.tooltipGroup = group
      this.tooltipLeftPct = Number(group?.positionPct || 0)
      this.previewReady = !this.activeVideoUrl
      this.$nextTick(() => {
        this.syncPreviewFrame()
      })
    },
    hideTooltip() {
      this.tooltipGroup = null
      this.previewReady = false
    },
    handlePointerLeave(event) {
      const nextTarget = event?.relatedTarget
      const shell = this.$refs.timelineShell
      const tooltip = this.$refs.timelineTooltip

      if (
        (shell && shell.contains(nextTarget)) ||
        (tooltip && tooltip.contains(nextTarget))
      ) {
        return
      }

      this.hideTooltip()
    },
    selectTooltipGroup() {
      if (!this.tooltipGroup) return
      this.$emit('select-group', this.tooltipGroup)
      this.hideTooltip()
    },
    handleMarkerClick(group) {
      this.$emit('select-group', group)

      if (this.isZoomed && this.zoomedGroupId === group.id) {
        this.resetZoom()
        return
      }

      const groupSpan = Math.max(1, Number(group.endSeconds) - Number(group.startSeconds))
      const targetDuration = Math.min(
        this.safeDuration,
        Math.max(90, Math.min(420, (groupSpan * 2.5) + 45))
      )
      this.setViewWindow(group.centerSeconds, targetDuration)
      this.zoomedGroupId = group.id
    },
    handleTimelineClick(event) {
      const shell = this.$refs.timelineShell
      if (!shell) return

      if (this.isZoomed) {
        this.resetZoom()
        return
      }

      const rect = shell.getBoundingClientRect()
      const clickRatio = rect.width > 0
        ? Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width))
        : 0.5
      const centerSeconds = this.viewStartSeconds + (this.viewDurationSeconds * clickRatio)
      const targetDuration = Math.min(this.safeDuration, Math.max(90, this.safeDuration * 0.25))

      this.setViewWindow(centerSeconds, targetDuration)
      this.zoomedGroupId = ''
    },
    setViewWindow(centerSeconds, durationSeconds) {
      const totalDuration = this.safeDuration
      const clampedDuration = Math.min(totalDuration, Math.max(1, durationSeconds))
      let startSeconds = Math.max(0, centerSeconds - (clampedDuration / 2))
      let endSeconds = startSeconds + clampedDuration

      if (endSeconds > totalDuration) {
        endSeconds = totalDuration
        startSeconds = Math.max(0, endSeconds - clampedDuration)
      }

      this.viewStartSeconds = startSeconds
      this.viewEndSeconds = Math.max(startSeconds + 1, endSeconds)

      if (this.tooltipGroup) {
        const refreshedTooltipGroup = this.visibleTimelineMarkers.find(group => group.id === this.tooltipGroup.id)
        if (refreshedTooltipGroup) {
          this.showTooltip(refreshedTooltipGroup)
        } else {
          this.hideTooltip()
        }
      }
    },
    resetZoom() {
      this.viewStartSeconds = 0
      this.viewEndSeconds = this.safeDuration
      this.zoomedGroupId = ''
    },
    syncPreviewFrame() {
      if (!this.tooltipGroup || !this.activeVideoUrl) return
      const video = this.$refs.previewVideo
      const previewVideo = Array.isArray(video) ? video[0] : video
      if (!previewVideo) return

      const targetSeconds = Number(this.tooltipGroup.centerSeconds || 0)
      const seek = () => {
        const duration = Number(previewVideo.duration)
        const safeTarget = Number.isFinite(duration) && duration > 0
          ? Math.min(Math.max(targetSeconds, 0), Math.max(0, duration - 0.1))
          : Math.max(targetSeconds, 0)
        this.previewReady = false
        previewVideo.currentTime = safeTarget
      }

      if (Number.isFinite(previewVideo.duration) && previewVideo.duration > 0) {
        seek()
        return
      }

      previewVideo.addEventListener('loadedmetadata', seek, { once: true })
      previewVideo.load()
    },
  },
}
</script>

<style scoped>
.timeline-overview {
  position: relative;
}

.timeline-zoom-reset {
  margin-top: 0.5rem;
  padding: 0.375rem 0.625rem;
  border: 1px solid rgba(15, 23, 42, 0.12);
  border-radius: .25rem;
  background: #ffffff;
  color: #64748b;
  font-size: 0.75rem;
  font-weight: 700;
}

.timeline-shell {
  position: relative;
  height: 6.5rem;
  border: 1px solid #dbe4f0;
  border-radius: .25rem;
  background:rgba(255, 255, 255, 1);
  overflow: visible;
}

.timeline-lane {
  position: absolute;
  left: 1.125rem;
  right: 1.125rem;
  border-radius: 999px;
}

.timeline-lane-primary {
  top: 3.625rem;
  height: 0.875rem;
  background: rgba(226, 232, 240, 0.95);
  box-shadow: inset 0 0 0 1px rgba(148, 163, 184, 0.16);
}

.timeline-lane-secondary {
  top: 2.125rem;
  height: 0.25rem;
  background: rgba(226, 232, 240, 0.85);
}

.timeline-gridline {
  position: absolute;
  top: 1.125rem;
  bottom: 1rem;
  width: 1px;
  background: rgba(148, 163, 184, 0.06);
}

.timeline-gridline:first-of-type {
  left: 1.125rem !important;
}

.timeline-gridline:last-of-type {
  left: calc(100% - 1.125rem) !important;
}

.timeline-marker {
  position: absolute;
  top: 3.25rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.375rem;
  padding: 0;
  border: 0;
  background: transparent;
  z-index: 1;
  transform: translate(-50%, -50%);
  transition: transform 0.2s ease;
}

.timeline-marker:hover,
.timeline-marker:focus-visible {
  z-index: 4;
  transform: translate(-50%, calc(-50% - 0.125rem));
}

.timeline-marker-core {
  display: block;
  width: 100%;
  min-width: 0.875rem;
  height: 1.375rem;
  border-radius: 0.35rem;
  border: 1px solid rgba(255, 255, 255, 0.9);
  background: #94a3b8;
  box-shadow: 0 0.5rem 0.875rem rgba(15, 23, 42, 0.08);
}

.timeline-marker-group .timeline-marker-core {
  background: #cbd5e1;
}

.timeline-marker-active .timeline-marker-core {
  background: #56595f;
  box-shadow: 0 0 0 0.25rem rgba(15, 23, 42, 0.08);
}

.timeline-marker-active {
  z-index: 3;
}

/* .timeline-marker-selected .timeline-marker-core {
  background: #0f172a;
} */

.timeline-marker-label {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  border-radius: .25rem;
  padding: 0.25rem 0.5rem;
  border: 1px solid rgba(226, 232, 240, 0.95);
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 0.375rem 0.75rem rgba(15, 23, 42, 0.04);
}

.timeline-marker-time {
  font-size: 0.7rem;
  font-weight: 700;
  color: #64748b;
}

.timeline-marker-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.25rem;
  height: 1.25rem;
  padding: 0 0.375rem;
  border-radius: .25rem;
  background: rgba(226, 232, 240, 0.9);
  color: #475569;
  font-size: 0.7rem;
  font-weight: 800;
  line-height: 1;
}

.timeline-tooltip {
  position: absolute;
  top: -10.5rem;
  z-index: 10;
  width: 11rem;
  cursor: pointer;
  overflow: hidden;
  border: 1px solid rgba(226, 232, 240, 0.95);
  border-radius: .25rem;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 1.125rem 2.25rem rgba(15, 23, 42, 0.12);
  transform: translateX(-50%);
}

.timeline-tooltip-frame {
  position: relative;
  aspect-ratio: 16 / 9;
  background: #0f172a;
}

.timeline-tooltip-video,
.timeline-tooltip-fallback {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.timeline-tooltip-fallback,
.timeline-tooltip-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.84);
  font-size: 0.78rem;
}

.timeline-tooltip-loading {
  position: absolute;
  inset: 0;
  background: #64748b;
}

.timeline-tooltip-body {
  padding: 0.75rem;
}

.timeline-tooltip-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.timeline-tooltip-timestamp {
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #0f172a;
}

.timeline-tooltip-badge {
  font-size: 0.68rem;
  font-weight: 700;
  color: #64748b;
}

.timeline-tooltip-title {
  margin: 0.5rem 0 0.25rem;
  font-size: 0.84rem;
  font-weight: 700;
  line-height: 1.35;
  color: #0f172a;
}

.timeline-tooltip-copy {
  margin: 0;
  font-size: 0.76rem;
  color: #64748b;
}

.timeline-scale {
  display: flex;
  justify-content: space-between;
  margin-top: 0.625rem;
  padding: 0 0.125rem;
}

.timeline-scale-tick {
  font-size: 0.74rem;
  color: #64748b;
}

@media (max-width: 767px) {
  .timeline-shell {
    height: 7rem;
  }

  .timeline-tooltip {
    top: -9.375rem;
    width: 10rem;
  }
}
</style>
